/**
 * Attachment upload for in-meeting chat.
 *
 * The only part of chat that can't ride the socket — a multipart body has to go
 * over HTTP. It uploads to the inMeet backend's existing `/api/v1/attachments`
 * (S3-backed), then the returned descriptor travels on the channel like any
 * other message field.
 *
 * Two deliberate choices:
 *
 *  - The **room** token, not the app token and not the SDK session token. This
 *    used to post to the messenger's `/api/v1/attachments`, which is guarded by
 *    `requireAuth` — so guests, who hold a room token and no app token, could
 *    not attach anything at all and had the button hidden. The room token is
 *    the tighter claim anyway: an app token proves someone signed up, a room
 *    token proves Phoenix admitted them to THIS room. (Pointing an SDK session
 *    token at either route 401s — a bug that already cost this codebase a
 *    debugging session, recorded in the SDK checkpoint doc.)
 *  - Its own uploader rather than importing `messengerApi`. Meeting chat and
 *    the org/tenant messenger stay independent features; sharing the endpoint
 *    is fine, sharing a module means a change to one can break the other.
 */

import { config } from '@/config/env'
import { roomFetch, uploadFailureMessageFor, MeetingEndedError, uploadUnreachableMessage } from '@/features/meeting/services/room-token'
import type { SkipNetworkErrorInit } from '@/clientWeb/components/ui/network-error-dialog'
import type { ChatAttachment, ChatAttachmentKind } from '../models/chat.model'

const API = config.apiUrl

type HostAttachment = {
  id: string
  mime: string
  sizeBytes: number
  filename: string | null
  url?: string
}

/**
 * The app (host-backend) bearer. Empty string when signed out — i.e. a guest.
 *
 * No longer used for uploading; kept because the local-disk `/raw` download
 * proxy is still app-authed. See `canUploadAttachments`.
 */
export function appToken(): string {
  try {
    const raw =
      localStorage.getItem('inmeet_auth_session') ||
      sessionStorage.getItem('inmeet_auth_session')
    if (!raw) return ''
    const parsed = JSON.parse(raw) as { session?: { accessToken?: string; expiresAt?: number } }
    const session = parsed?.session
    if (session?.expiresAt && session.expiresAt <= Date.now()) return ''
    return session?.accessToken || ''
  } catch {
    return ''
  }
}

/**
 * True when this client can upload at all.
 *
 * Now asks "do I hold a room token", which every participant does INCLUDING
 * guests — it used to ask "am I signed in", which is why the attach button was
 * missing for guests regardless of the host's File Sharing setting.
 */
export function canUploadAttachments(roomToken: string | null | undefined): boolean {
  return !!roomToken
}

/** The local backend hands back a relative `/raw` path; S3 gives an absolute URL. */
function absolutize(url: string): string {
  if (/^https?:\/\//i.test(url)) return url
  return `${API}${url.startsWith('/') ? '' : '/'}${url}`
}

/**
 * Upload one file and return the descriptor to attach to a message.
 *
 * Throws on failure so the caller can tell the user the file didn't send —
 * unlike history, a silently dropped attachment looks like a delivered one.
 */
export async function uploadChatAttachment(
  file: File,
  kind: ChatAttachmentKind,
  roomId: string,
  roomToken: string,
): Promise<ChatAttachment> {
  if (!roomToken) throw new Error('Not connected to the meeting')

  const form = new FormData()
  form.append('file', file)
  // Private: a file sent in the meeting's chat is for the people in it, opened
  // through a link signed for the room (VAPT 2026-10).
  form.append('private', 'true')

  let res: Response
  try {
    res = await roomFetch(roomId, roomToken, `${API}/api/v1/meeting-chat/${encodeURIComponent(roomId)}/attachments`, {
      method: 'POST',
      credentials: 'include',
      // No Content-Type: the browser has to set the multipart boundary itself.
      body: form,
      // This reports its own failure; see SkipNetworkErrorInit.
      skipNetworkError: true,
    } as SkipNetworkErrorInit)
  } catch (err) {
    if (err instanceof MeetingEndedError) throw err
    throw new Error(await uploadUnreachableMessage())
  }

  if (!res.ok) throw new Error(await uploadFailureMessageFor(res))

  const body = (await res.json()) as { attachment?: HostAttachment }
  const att = body.attachment
  if (!att?.id) throw new Error('Upload failed')

  // `url` is absent on the local-disk backend, so fall back to the proxy that
  // serves it — the MEETING-CHAT one, keyed by room.
  //
  // It used to fall back to the messenger's `/api/v1/attachments/:id/raw`,
  // copied from a path that runs with an app access token. Meeting chat runs on
  // a ROOM token, which that route rejects: the file uploaded, and then every
  // attempt to fetch it answered 401. The upload looked like it had worked and
  // the attachment was simply never there.
  const url = att.url
    ? absolutize(att.url)
    : `${API}/api/v1/meeting-chat/${encodeURIComponent(roomId)}/attachments/${att.id}/raw`

  return {
    kind,
    url,
    name: att.filename || file.name,
    size: att.sizeBytes ?? file.size,
    mimeType: att.mime || file.type,
  }
}
